import { isEmpty } from '~libs.typescript';

import { Reimbursement } from '../types';

import { hasEligibleReimbursementCode } from './reimbursement';

const TREATMENT_DAYS_COUNT_MIN_VALUE = 1;
const SINGLE_PACKAGE_QUANTITY = 1;
const TREATMENT_DAYS_COUNT_ISSUED_FOR_YEAR_MAX_VALUE = 360;
const TREATMENT_DAYS_COUNT_STANDARD_MAX_VALUE = 120;
const TREATMENT_DAYS_COUNT_DEFAULT_VALUE = null;

export type TreatmentDaysCountLimitOptions = {
   issuedForYear: boolean;
};

export type TreatmentSupervisionOptions = TreatmentDaysCountLimitOptions & {
   reimbursement: Reimbursement | null | undefined;
   packageCapacityKnown: boolean;
};

export type TreatmentDaysCountExceededOptions = TreatmentDaysCountLimitOptions & {
   treatmentDaysCount: number | null | undefined;
   medicineSufficiencyCount: number | null | undefined;
   reimbursement: Reimbursement | null | undefined;
   packagesCount: number | null | undefined;
};

export const getTreatmentDaysCountMinValue = (): number => {
   return TREATMENT_DAYS_COUNT_MIN_VALUE;
};

export const getTreatmentDaysCountMaxValue = (options: TreatmentDaysCountLimitOptions): number => {
   return options.issuedForYear
      ? TREATMENT_DAYS_COUNT_ISSUED_FOR_YEAR_MAX_VALUE
      : TREATMENT_DAYS_COUNT_STANDARD_MAX_VALUE;
};

export const isUnderTreatmentSupervision = (options: TreatmentSupervisionOptions): boolean => {
   if (!options.packageCapacityKnown) {
      return false;
   }

   return options.issuedForYear || hasEligibleReimbursementCode(options.reimbursement?.code);
};

export const getEffectiveTreatmentDaysCount = (options: TreatmentDaysCountExceededOptions): number | null => {
   return options.treatmentDaysCount ?? options.medicineSufficiencyCount ?? null;
};

const hasSmallestPackageException = (options: TreatmentDaysCountExceededOptions): boolean => {
   if (options.issuedForYear || hasEligibleReimbursementCode(options.reimbursement?.code)) {
      return false;
   }

   return options.packagesCount === SINGLE_PACKAGE_QUANTITY;
};

export const isTreatmentDaysCountExceeded = (options: TreatmentDaysCountExceededOptions): boolean => {
   const effectiveTreatmentDaysCount = getEffectiveTreatmentDaysCount(options);

   if (isEmpty(effectiveTreatmentDaysCount) || hasSmallestPackageException(options)) {
      return false;
   }

   return effectiveTreatmentDaysCount > getTreatmentDaysCountMaxValue(options);
};

export const isTreatmentDaysCountExceedingYearLimit = (options: TreatmentDaysCountExceededOptions): boolean => {
   const effectiveTreatmentDaysCount = getEffectiveTreatmentDaysCount(options);

   if (isEmpty(effectiveTreatmentDaysCount)) {
      return false;
   }

   return effectiveTreatmentDaysCount > TREATMENT_DAYS_COUNT_ISSUED_FOR_YEAR_MAX_VALUE;
};

export const getExceededTreatmentDaysCountMaxValue = (options: TreatmentDaysCountExceededOptions): number => {
   return isTreatmentDaysCountExceedingYearLimit(options)
      ? TREATMENT_DAYS_COUNT_ISSUED_FOR_YEAR_MAX_VALUE
      : getTreatmentDaysCountMaxValue(options);
};

export const getTreatmentDaysCountDefaultValue = (
   defaultValue: number | null | undefined,
   medicineSufficiencyCount: number | undefined
): number | null => {
   if (!isEmpty(defaultValue)) {
      return defaultValue;
   }

   if (isEmpty(medicineSufficiencyCount)) {
      return TREATMENT_DAYS_COUNT_DEFAULT_VALUE;
   }

   return Math.max(TREATMENT_DAYS_COUNT_MIN_VALUE, medicineSufficiencyCount);
};

export const getAutoTreatmentDaysCount = (medicineSufficiencyCount: number | undefined): number | null => {
   if (medicineSufficiencyCount === undefined) {
      return null;
   }

   return Math.max(TREATMENT_DAYS_COUNT_MIN_VALUE, medicineSufficiencyCount);
};
